import { useWebsite } from '../context/WebsiteContext';
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Search } from 'lucide-react';
import { useSmartContent } from '../context/SmartSiteContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Brand color tokens ─── */
const OFFICIAL_PILL = { bg: '#1B3A5C', text: '#F5A623' };   // Blue bg / Yellow text
const OTHER_PILL    = { bg: '#F5A623', text: '#1B3A5C' };   // Yellow bg / Blue text
const CHOOSE_PILL   = { bg: '#4A90A4', text: '#ffffff' };

/* ─── Reveal wrapper ─── */
const Reveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { rootMargin: '50px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      className={`transition-all duration-600 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
      {children}
    </div>
  );
};

/* ─── School pill button ─── */
const SchoolPill = ({ school, variant }) => {
  const colors = variant === 'official' ? OFFICIAL_PILL : OTHER_PILL;
  const name = school.school_name || school.name;
  const isOfficial = variant === 'official';
  // Official → school product listing.
  // Other (non-official) → the $15 School Logo Application product, carrying school name in state.
  const to = isOfficial
    ? `/school/${school.slug}`
    : '/product/school-logo-application';
  const state = isOfficial ? undefined : { schoolName: name, schoolSlug: school.slug };
  return (
    <Link
      to={to}
      state={state}
      className="px-6 py-3 rounded-lg font-bold text-sm uppercase tracking-wide shadow-sm hover:shadow-md hover:scale-105 transition-all"
      style={{ backgroundColor: colors.bg, color: colors.text }}
      data-testid={`school-${school.slug}`}
    >
      {name}
    </Link>
  );
};

/* ─── Staggered row layout (3, 4, 3, 4, ...) ─── */
const buildRows = (items) => {
  const rows = [];
  const pattern = [3, 4, 3, 4];
  let idx = 0;
  let patIdx = 0;
  while (idx < items.length) {
    const count = pattern[patIdx % pattern.length];
    rows.push(items.slice(idx, idx + count));
    idx += count;
    patIdx++;
  }
  return rows;
};

const BackToSchoolPage = () => {
  const { config: website } = useWebsite();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const btsData = useSmartContent('bts-hero', 'hero');
  const b = btsData.items?.[0]?.data || {};

  useEffect(() => {
    axios.get(`${API_URL}/api/categories/schools`).then(r => setSchools(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const officialSchools = schools.filter(s => s.is_official !== false);
  const otherSchools    = schools.filter(s => s.is_official === false);

  const officialRows = buildRows(officialSchools);
  const otherRows    = buildRows(otherSchools);

  const q = query.trim().toLowerCase();
  const searchMatches = q
    ? schools.filter((s) => {
        const name = (s.school_name || s.name || '').toLowerCase();
        const slug = (s.slug || '').toLowerCase();
        return name.includes(q) || slug.includes(q);
      })
    : [];

  return (
    <div className="min-h-screen bg-white" data-testid="back-to-school-page">

      {/* ═══ Hero Section — Back 2 School graphic + benefit copy ═══ */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #2B5EA7 0%, #3B7CC9 50%, #2B5EA7 100%)' }}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg className="absolute top-8 right-8 w-16 h-16 text-white/10" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M12 4l1.5 4.5H18l-3.5 2.8L16 16l-4-3-4 3 1.5-4.7L6 8.5h4.5z" /></svg>
          <svg className="absolute bottom-16 right-32 w-12 h-12 text-white/10" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M12 4l1.5 4.5H18l-3.5 2.8L16 16l-4-3-4 3 1.5-4.7L6 8.5h4.5z" /></svg>
          <svg className="absolute bottom-8 right-8 w-24 h-24 text-white/8" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M4 6h14l-2 12H6L4 6zm2 0V4a2 2 0 012-2h6a2 2 0 012 2v2" /></svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="flex justify-center lg:justify-start">
              <img
                src={b.image || "/images/back-to-school/back-2-school-hero.png"}
                alt="Back 2 School — Stitched with love by Kre8ing Your IdeaS"
                className="w-full max-w-lg lg:max-w-xl h-auto drop-shadow-2xl"
                loading="eager"
                data-testid="bts-hero-image"
              />
            </div>

            <div className="text-white lg:pl-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight mb-5">
                {b.heading || <>School Uniform<br />Season Made Easy</>}
              </h1>
              <p className="text-lg sm:text-xl text-white/85 leading-relaxed mb-8 max-w-lg">
                {b.body || 'Order official school uniforms, embroidery, and pickup options all in one place.'} <span className="text-[#F5A623]">♥</span>
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href={b.button_link || "#select-school"}
                  className="inline-flex items-center justify-center px-8 py-4 font-bold text-sm uppercase tracking-wide rounded-lg shadow-lg transition-all hover:brightness-110"
                  style={{ backgroundColor: '#F5A623', color: '#1B3A5C' }}
                  data-testid="bts-hero-shop-btn"
                >
                  {b.button_text || 'Shop Uniforms'}
                </a>
                <a href={b.secondary_link || "tel:443-940-6660"}
                  className="inline-flex items-center justify-center px-8 py-4 font-bold text-sm uppercase tracking-wide rounded-lg border-2 transition-all hover:bg-white/10"
                  style={{ borderColor: 'rgba(255,255,255,0.6)', color: '#fff' }}
                  data-testid="bts-hero-phone-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {b.secondary_text || 'Text Us 443.940.6660'}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="relative h-20 -mb-px">
          <svg viewBox="0 0 1440 80" className="absolute bottom-0 w-full" preserveAspectRatio="none">
            <path d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,50 1440,40 L1440,80 L0,80 Z" fill="white" />
            <path d="M0,32 C360,72 720,-8 1080,32 C1260,52 1380,42 1440,32" fill="none" stroke="#F5A623" strokeWidth="2" strokeDasharray="6 6" />
          </svg>
        </div>
      </section>

      {/* ═══ Select Your School ═══ */}
      <section id="select-school" className="relative py-20 bg-white overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <img
            src={website.logo}
            alt=""
            className="min-w-[1400px] h-auto translate-y-12"
            style={{ opacity: 0.12 }}
          />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-8 text-[#1B3A5C]">
              Select Your School To See Uniform Options
            </h2>
          </Reveal>

          {/* Search bar */}
          <Reveal>
            <div className="max-w-md mx-auto mb-10">
              <label className="sr-only" htmlFor="school-search">Search for your school</label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                <input
                  id="school-search"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search for your school..."
                  className="w-full pl-11 pr-4 py-3 rounded-full border-2 border-gray-200 focus:border-[#4A90A4] focus:ring-2 focus:ring-[#4A90A4]/20 focus:outline-none text-sm text-gray-900 shadow-sm bg-white"
                  data-testid="school-search-input"
                />
              </div>
              <p className="text-center text-xs text-gray-500 mt-2">
                Type your school&apos;s name — including schools we don&apos;t stock yet.
              </p>
            </div>
          </Reveal>

          {loading ? (
            <div className="flex flex-wrap justify-center gap-5">
              {[...Array(12)].map((_, i) => <div key={i} className="h-12 w-44 bg-gray-100 rounded-lg animate-pulse" />)}
            </div>
          ) : q ? (
            /* Search results view */
            searchMatches.length > 0 ? (
              <div className="flex flex-wrap justify-center gap-3" data-testid="school-search-results">
                {searchMatches.map((school) => (
                  <SchoolPill
                    key={school.id}
                    school={school}
                    variant={school.is_official === false ? 'other' : 'official'}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-10" data-testid="school-search-empty">
                <p className="text-gray-500 mb-3">No school matches &ldquo;{query}&rdquo;.</p>
                <Link
                  to="/product/school-logo-application"
                  state={{ schoolName: query }}
                  className="inline-flex items-center px-6 py-2.5 rounded-full font-semibold text-sm hover:brightness-110 transition-all"
                  style={{ backgroundColor: OTHER_PILL.bg, color: OTHER_PILL.text }}
                  data-testid="school-search-empty-cta"
                >
                  Bring your own shirt · $15 logo application
                </Link>
              </div>
            )
          ) : (
            <>
              {/* ── Official Schools ── */}
              {officialSchools.length > 0 && (
                <div className="mb-14" data-testid="official-schools-section">
                  <Reveal>
                    <div className="flex items-center justify-center gap-3 mb-6">
                      <span className="inline-block w-8 h-[2px] bg-[#1B3A5C]" />
                      <h3 className="text-lg sm:text-xl font-extrabold uppercase tracking-[0.15em] text-[#1B3A5C]">
                        Official Schools
                      </h3>
                      <span className="inline-block w-8 h-[2px] bg-[#1B3A5C]" />
                    </div>
                    <p className="text-center text-sm text-gray-500 mb-8 max-w-xl mx-auto">
                      KYIS is the official uniform vendor — shop stocked sizes, colors, and bundles.
                    </p>
                  </Reveal>
                  <div className="space-y-5">
                    {officialRows.map((row, ri) => (
                      <Reveal key={`off-${ri}`} delay={ri * 60}>
                        <div className="flex flex-wrap justify-center gap-4">
                          {row.map((school) => (
                            <SchoolPill key={school.id} school={school} variant="official" />
                          ))}
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Other Schools ── */}
              {otherSchools.length > 0 && (
                <div className="mb-4" data-testid="other-schools-section">
                  <Reveal>
                    <div className="flex items-center justify-center gap-3 mb-6">
                      <span className="inline-block w-8 h-[2px] bg-[#F5A623]" />
                      <h3 className="text-lg sm:text-xl font-extrabold uppercase tracking-[0.15em] text-[#F5A623]">
                        Other Schools
                      </h3>
                      <span className="inline-block w-8 h-[2px] bg-[#F5A623]" />
                    </div>
                    <p className="text-center text-sm text-gray-500 mb-8 max-w-2xl mx-auto">
                      Bring your own shirt — we&apos;ll apply your school logo for
                      <span className="font-semibold text-[#1B3A5C]"> $15 per shirt</span>. In-store visit required.
                    </p>
                  </Reveal>
                  <div className="space-y-5">
                    {otherRows.map((row, ri) => (
                      <Reveal key={`oth-${ri}`} delay={ri * 40}>
                        <div className="flex flex-wrap justify-center gap-4">
                          {row.map((school) => (
                            <SchoolPill key={school.id} school={school} variant="other" />
                          ))}
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              )}

              {/* Choose your school pill */}
              <Reveal delay={200}>
                <div className="flex justify-center mt-10">
                  <Link
                    to="/contact"
                    className="px-6 py-3 rounded-lg font-bold text-sm uppercase tracking-wide shadow-sm hover:shadow-md hover:scale-105 transition-all"
                    style={{ backgroundColor: CHOOSE_PILL.bg, color: CHOOSE_PILL.text }}
                    data-testid="school-choose-your-school"
                  >
                    Don&apos;t see your school? Contact us
                  </Link>
                </div>
              </Reveal>
            </>
          )}

          <Reveal delay={400}>
            <p className="text-center mt-14 font-semibold text-[#1B3A5C] leading-relaxed max-w-2xl mx-auto">
              We Are Pleased To Offer You The Flexibility Of Bringing Your Own Shirt For School Uniforms. <span className="text-[#F5A623]">$15 Per Shirt</span> Please Note That This Service Requires An In-Store Visit.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══ Family Message ═══ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="text-3xl font-bold text-center mb-10 text-[#4A90A4] italic">A message from our family to yours...</h2>
          </Reveal>
          <Reveal delay={100}>
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="md:w-48 flex-shrink-0">
                <img src={website.logo} alt="KYIS Logo" className="w-full max-w-[180px] mx-auto" />
              </div>
              <div className="flex-1 space-y-4 text-gray-600 leading-relaxed">
                <p><strong className="text-[#F5A623] text-lg">Hey there, Fellow Kre8ives!</strong></p>
                <p>It&apos;s that time of year again! Our young scholars will be heading back to school in just a few short weeks.</p>
                <p>Let us take those school uniforms off of your &ldquo;To Do&rdquo; List. We are stocked and ready to meet all of your school uniform and embroidery needs.</p>
                <p>Here&apos;s to wishing our young scholars a safe, educational and fun-filled school year!</p>
                <p className="font-medium">Stay safe. Stay healthy. Stay kre8ive!</p>
                <div className="pt-4 border-t border-gray-100">
                  <p className="font-bold text-[#1B3A5C]">Gabrielle McLeod</p>
                  <p className="text-sm text-gray-500">Executive Director of Operations</p>
                  <p className="text-sm text-gray-500">Kre8ing Your IdeaS</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ uniforms • apparel • accessories ═══ */}
      <section className="py-20 bg-[#FDF6E9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="text-3xl font-bold text-center mb-2 text-[#1B3A5C]">uniforms &bull; apparel &bull; accessories</h2>
            <div className="w-20 h-1 bg-[#F5A623] mx-auto mt-4 mb-16 rounded-full" />
          </Reveal>

          <Reveal>
            <div className="flex flex-col md:flex-row gap-10 items-center mb-20">
              <div className="md:w-1/2">
                <img src="https://shopkyis.com/wp-content/uploads/2024/12/Kipp-Ujima-Blue.png" alt="School Uniforms" className="rounded-2xl shadow-lg w-full max-w-md mx-auto" />
              </div>
              <div className="md:w-1/2">
                <h3 className="text-2xl font-bold text-[#1B3A5C] mb-4">School Uniforms</h3>
                <p className="text-gray-600 leading-relaxed mb-4">Bring your own shirt or purchase one of ours! Uniforms are available for purchase in-store in a variety of sizes and colors for schools where KYIS is the official vendor.</p>
                <p className="text-gray-600 leading-relaxed">Any other schools, sizes, or colors can be Kre8ed and ready for pick up within 5 business days.</p>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="flex flex-col md:flex-row-reverse gap-10 items-center mb-20">
              <div className="md:w-1/2">
                <img src="https://shopkyis.com/wp-content/uploads/2024/12/IMG_3152_edited_edited.jpg" alt="School Apparel" className="rounded-2xl shadow-lg w-full max-w-md mx-auto" />
              </div>
              <div className="md:w-1/2">
                <h3 className="text-2xl font-bold text-[#1B3A5C] mb-4">School Apparel & Accessories</h3>
                <p className="text-gray-600 leading-relaxed mb-4">Choose from our extensive catalog with everything from varsity jackets to book bags.</p>
                <p className="text-gray-600 leading-relaxed">Trendy, dress code-friendly options are available so your student can show off their unique style and personality!</p>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="flex flex-col md:flex-row gap-10 items-center">
              <div className="md:w-1/2">
                <img src="https://shopkyis.com/wp-content/uploads/2024/12/Untitled-design-67.png" alt="Custom Embroidery" className="rounded-2xl shadow-lg w-full max-w-md mx-auto" />
              </div>
              <div className="md:w-1/2">
                <h3 className="text-2xl font-bold text-[#1B3A5C] mb-4">Custom Embroidery</h3>
                <p className="text-gray-600 leading-relaxed mb-4">Have any piece of clothing or accessory personalized so your student has a one-of-one kre8tion! Bring your own or purchase an item from our catalog.</p>
                <p className="text-gray-600 leading-relaxed"><strong>Popular Customizations include:</strong> Name, Initials, Nickname, Sports, Number, & Graduation Year</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ CTA Footer ═══ */}
      <section className="py-16 bg-[#1B3A5C]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white">TEXT 443-940-6660 TO CHECK ORDER STATUS</h2>
            <p className="text-white/50 mb-8 text-sm">Visit us at 129 Slade Ave, Pikesville, MD 21208</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-[#F5A623] text-gray-900 font-bold rounded-full hover:brightness-110 transition-all">
                Contact Us <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <a href="tel:443-940-6660" className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/30 text-white font-bold rounded-full hover:bg-white/10 transition-all">
                <Phone className="w-4 h-4 mr-2" /> Call Us
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default BackToSchoolPage;
